import { useTranslation } from 'react-i18next';
import type { DealStage } from '@features/deals/constants/deal-stages';

/**
 * The five columns the board shows, and the stages each one covers.
 *
 * The column is a *reading* of the stage, not a second field stored
 * beside it. That is the whole point: a client's position used to live
 * in `metadata.status` and be synced to the deal's stage best-effort, so
 * the two could disagree and nothing noticed. Derived, they cannot.
 *
 * `pausados` is the exception to "one column per phase of the work": it
 * is not a phase, it is work set aside. It was left off the board
 * entirely, on the reasoning that nobody works from it — but the stage
 * filter is derived from these columns, so leaving it out did not put a
 * paused attendance behind a filter, it put it nowhere. Pausing removed
 * a card from the board *and* from the table, silently, with no total
 * saying so. It is here so the work exists somewhere, and
 * {@link isColumnWorthShowing} keeps it off the board on the days there
 * is nothing in it.
 */
export const ATTENDANCE_COLUMNS = [
  { id: 'novos', stages: ['new'] },
  // `visit_scheduled` used to share this column with `qualified`, which
  // hid the single most concrete thing a broker does: a booked visit
  // read the same as a client merely being worked.
  { id: 'conversando', stages: ['contacted'] },
  { id: 'visitando', stages: ['visiting'] },
  { id: 'negociando', stages: ['negotiating'] },
  { id: 'ganho', stages: ['won'] },
  { id: 'encerrado', stages: ['lost'] },
  { id: 'pausados', stages: ['paused'] },
] as const satisfies ReadonlyArray<{ id: string; stages: readonly DealStage[] }>;

/**
 * Whether a column earns its place on the board right now.
 *
 * Only `pausados` can fail this. The five phase columns are the shape
 * of the funnel and an empty one is information -- nothing new this
 * week is worth seeing. A paused column is not part of that shape, so
 * an empty one is just a sixth of the width spent on nothing; it
 * appears when it holds something, or when the reader asked for it by
 * name in the stage filter.
 *
 * @param id - The column being considered.
 * @param count - Its server-side total.
 * @param requested - Whether the stage filter names one of its stages.
 * @returns True when the column should be rendered.
 */
export function isColumnWorthShowing(
  id: AttendanceColumnId, count: number, requested: boolean,
): boolean {
  if (id !== 'pausados') return true;
  return count > 0 || requested;
}

export type AttendanceColumnId = (typeof ATTENDANCE_COLUMNS)[number]['id'];

/**
 * The columns that are not steps of the funnel.
 *
 * A lost attendance and a paused one are not positions in the pipeline
 * -- they are work out of play. Standing them in the same row as Novos
 * through Ganho made the board read as a seven-step funnel that ends in
 * a loss, which is the opposite of what the row is for. They keep their
 * columns, set apart at the far right.
 */
export const OUT_OF_PLAY_COLUMNS: readonly AttendanceColumnId[] = ['encerrado', 'pausados'];

/**
 * Whether a column is a step of the funnel rather than work set aside.
 *
 * @param id - The column being placed.
 * @returns True for the funnel steps.
 */
export function isFunnelColumn(id: AttendanceColumnId): boolean {
  return !OUT_OF_PLAY_COLUMNS.includes(id);
}

/**
 * Accent tones per column, reusing the badge palette.
 *
 * All four tinted headers mix at 14%, the one chip strength the approved
 * prototype uses everywhere it tints an accent. They were 16/14/16/14,
 * which made "Conversando" and "Ganho" sit a shade heavier than their
 * neighbours for no reason a reader could name.
 *
 * `novos` stays neutral: the prototype's stage→accent rule reads `lead`
 * as mostarda, the same tone as `qualified`/`visit_scheduled`, so
 * following it here would paint the first two columns identically and
 * cost the board its leftmost boundary. The cards inside still carry the
 * prototype's mostarda on their stage pill.
 */
export const ATTENDANCE_COLUMN_STYLES: Record<
  AttendanceColumnId, { bg: string; color: string }
> = {
  novos: {
    bg: 'var(--surface-tertiary)',
    color: 'var(--text-secondary)',
  },
  conversando: {
    bg: 'color-mix(in srgb, var(--accent-mostarda) 14%, var(--surface-primary))',
    color: 'var(--accent-mostarda)',
  },
  visitando: {
    bg: 'color-mix(in srgb, var(--accent-marinho) 14%, var(--surface-primary))',
    color: 'var(--accent-marinho)',
  },
  negociando: {
    bg: 'color-mix(in srgb, var(--accent-petroleo) 14%, var(--surface-primary))',
    color: 'var(--accent-petroleo)',
  },
  ganho: {
    bg: 'color-mix(in srgb, var(--accent-salvia) 14%, var(--surface-primary))',
    color: 'var(--accent-salvia)',
  },
  encerrado: {
    bg: 'color-mix(in srgb, var(--accent-cobre) 14%, var(--surface-primary))',
    color: 'var(--accent-cobre)',
  },
  // Neutral on purpose, and the only column besides `novos` that is.
  // Paused work is out of play: tinting it with the mostarda that
  // `paused` reads as elsewhere would give it the same weight as
  // "Conversando", which is the one thing it is not.
  pausados: {
    bg: 'var(--surface-tertiary)',
    color: 'var(--text-tertiary)',
  },
};

/**
 * Which column a stage belongs to, or null when it is off the board.
 *
 * @param stage - The attendance's current stage.
 * @returns The column id, or null for stages the board does not show.
 */
export function columnForStage(stage: string): AttendanceColumnId | null {
  const found = ATTENDANCE_COLUMNS.find(
    (column) => (column.stages as readonly string[]).includes(stage),
  );
  return found ? found.id : null;
}

/**
 * Resolve the display label for a column.
 *
 * @returns A translator from column id to localized label.
 */
export function useAttendanceColumnLabel(): (id: AttendanceColumnId) => string {
  const { t } = useTranslation('features/attendances');
  return (id) => t(`columns.${id}`);
}
